// src/documents/exportProcessoManipulacao.js

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import grupoLogo from '../assets/grupo_logo.png';

const PRODUCTS_COLLECTION = 'products';

/**
 * Loads and adds the company logo to the workbook
 */
const loadLogo = async (workbook) => {
    try {
        const logoResponse = await fetch(grupoLogo);
        if (!logoResponse.ok) {
            throw new Error(`Falha ao carregar o logo (status ${logoResponse.status}).`);
        }
        const blob = await logoResponse.blob();

        const mime = blob.type || 'image/png';
        let ext = mime.split('/').pop().toLowerCase() || 'png';

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
        if (!supportedExtensions.includes(ext)) {
            throw new Error(`Formato de imagem não suportado: "${ext}". Converta o logo para PNG ou JPEG.`);
        }

        if (ext === 'jpeg') ext = 'jpg';

        const logoImageId = workbook.addImage({ base64, extension: ext });

        if (logoImageId === undefined || logoImageId === null) {
            throw new Error('Logo não pôde ser adicionado à planilha.');
        }

        return logoImageId;
    } catch (error) {
        console.error('Logo loading error:', error);
        throw new Error(`Não foi possível carregar o logo: ${error.message}. A geração foi cancelada.`);
    }
};

/**
 * Creates a single product worksheet within the workbook
 */
const createProductSheet = (workbook, logoImageId, params) => {
    const {
        item,
        productData,
        allRawMaterials,
        allCategories,
        allItems,
        allProducts,
        allProductTypes,
        order,
        sheetIndex,
        manipulationData
    } = params;

    // Ensure all arrays are safe
    const safeAllRawMaterials = allRawMaterials || [];
    const safeAllCategories = allCategories || [];
    const safeAllItems = allItems || [];
    const safeAllProducts = allProducts || [];
    const safeAllProductTypes = allProductTypes || [];

    let sheetName = `${item.productCode}_${item.lote || 'SEMLOTE'}`;
    if (sheetName.length > 31) sheetName = sheetName.substring(0, 31);

    const worksheet = workbook.addWorksheet(sheetName);

    // 6 Columns Setup according to the new CSV structure
    worksheet.getColumn(1).width = 12;  // %
    worksheet.getColumn(2).width = 15;  // Código
    worksheet.getColumn(3).width = 40;  // Lote/Fornecedor
    worksheet.getColumn(4).width = 52;  // INCI Matéria Prima / Nome Comercial
    worksheet.getColumn(5).width = 10;  // UN
    worksheet.getColumn(6).width = 18;  // Quant.

    // Use manipulationData values if provided, otherwise calculate
    const calcKgl = manipulationData?.calculadaKgl ? parseFloat(manipulationData.calculadaKgl) : null;
    const perdaVal = manipulationData?.perda ? parseFloat(manipulationData.perda) : null;
    const progKgl = manipulationData?.programadaKgl ? parseFloat(manipulationData.programadaKgl) : null;

    // Calculations
    const mode = item.mode || 'mass';
    const quantity = parseFloat(item.quantity) || 0;
    const lossIndex = parseFloat(productData.lossIndex) || 1;
    const gramatura = parseFloat(productData.gramatura) || 0;
    const gramaturaUnit = productData.gramaturaUnit || 'g';

    const gramaturaBase = (gramaturaUnit === 'g' || gramaturaUnit === 'ml')
        ? gramatura / 1000
        : gramatura;

    let pesoCalculado, pesoProgramado, perdaPercentual;

    if (mode === 'mass') {
        pesoCalculado = calcKgl !== null ? calcKgl : quantity;
        perdaPercentual = perdaVal !== null ? perdaVal.toString() : ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = progKgl !== null ? progKgl : quantity * lossIndex;
    } else {
        pesoCalculado = calcKgl !== null ? calcKgl : quantity * gramaturaBase;
        perdaPercentual = perdaVal !== null ? perdaVal.toString() : ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = progKgl !== null ? progKgl : pesoCalculado * lossIndex;
    }

    // Use the raw materials from productData directly
    const rawMaterials = productData.rawMaterials || [];
    const preparationSteps = productData.preparationSteps || [];

    // Get packaging and serigrafia category IDs for exclusion
    const packagingCategoryIds = safeAllCategories
        .filter(c => c && ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
        .map(c => c.id);

    // Get semi-finished product type/category IDs for identification
    const semiFinishedCategoryIds = safeAllCategories
        .filter(c => c && ['SEMI ACABADO', 'SEMI-ACABADO'].includes(c.name?.toUpperCase()))
        .map(c => c.id);

    const semiFinishedTypeIds = safeAllProductTypes
        .filter(t => t && ['SEMI ACABADO', 'SEMI-ACABADO'].includes(t.name?.toUpperCase()))
        .map(t => t.id);

    // Build material data map
    const mpDataMap = {};
    for (const mp of rawMaterials) {
        if (!mp || !mp.id) continue;

        let productDataItem = safeAllProducts.find(p => p && p.id === mp.id);
        let isSemiFinished = false;

        if (productDataItem) {
            isSemiFinished = productDataItem.categoryIds?.some(id => semiFinishedCategoryIds.includes(id)) ||
                productDataItem.productTypeIds?.some(id => semiFinishedTypeIds.includes(id));
        }

        let itemData = safeAllItems.find(m => m && m.id === mp.id);
        let isPackaging = false;

        if (itemData) {
            isPackaging = itemData.categoryIds?.some(id => packagingCategoryIds.includes(id)) ||
                itemData.productTypeIds?.some(id => {
                    const type = safeAllProductTypes.find(t => t.id === id);
                    return type && ['SERIGRAFIA', 'EMBALAGEM'].includes(type.name?.toUpperCase());
                });
        }

        if (!itemData) {
            itemData = safeAllRawMaterials.find(m => m && m.id === mp.id);
        }

        if (!isPackaging) {
            if (isSemiFinished && productDataItem) {
                mpDataMap[mp.id] = {
                    id: productDataItem.id,
                    code: productDataItem.code || '',
                    inciName: productDataItem.inciName || '',
                    commercialName: productDataItem.commercialName || productDataItem.name || '',
                    isSemiFinished: true,
                    _quantity: mp.quantity
                };
            } else if (itemData) {
                mpDataMap[mp.id] = {
                    ...itemData,
                    isSemiFinished: false
                };
            }
        }
    }

    // Separate by phase
    const fases = {};
    rawMaterials.forEach(mp => {
        if (!mp || !mp.id || !mpDataMap[mp.id]) return;
        const fase = mp.fase || 'A';
        if (!fases[fase]) fases[fase] = [];
        const quantityFactor = parseFloat(mp.quantity) || 0;
        let needed = (pesoCalculado * quantityFactor) / 100;
        needed = needed * (1 + (parseFloat(perdaPercentual) || 0) / 100);
        fases[fase].push({
            ...mp,
            needed: needed,
            code: mpDataMap[mp.id]?.code || '',
            inciName: mp.inciName || mpDataMap[mp.id]?.inciName || '',
            commercialName: mpDataMap[mp.id]?.commercialName || '',
            isSemiFinished: mpDataMap[mp.id]?.isSemiFinished || false
        });
    });

    const faseKeys = Object.keys(fases).sort();
    let currentRow = 1;

    // ROW 1: Logo (merged A1:B1) + Title + Document Code
    const row1 = worksheet.getRow(currentRow);
    row1.height = 70;

    worksheet.mergeCells('A1:B1');

    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.1, row: 0.050 },
            ext: { width: 160, height: 85 },
            editAs: 'oneCell'
        });
    }

    worksheet.mergeCells('C1:E1');
    worksheet.getCell('C1').value = 'Processo de Manipulação\nOrdem de Produção';
    worksheet.getCell('C1').font = { bold: true, size: 14 };
    worksheet.getCell('C1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.getCell('F1').value = 'FQ 05\nRev. 04';
    worksheet.getCell('F1').font = { bold: true, size: 12 };
    worksheet.getCell('F1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    ['A', 'B', 'C', 'D', 'E', 'F'].forEach(col => {
        worksheet.getCell(`${col}1`).border = {
            top: { style: 'medium' }, left: { style: 'medium' },
            bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    });

    // Row 2: Checkboxes
    currentRow = 2;
    const row2 = worksheet.getRow(currentRow);
    row2.height = 30;

    worksheet.mergeCells('A2:B2');
    worksheet.getCell('A2').value = '☐ Desenvolvimento Aprovado';
    worksheet.mergeCells('C2:D2');
    worksheet.getCell('C2').value = '☐ Desenvolvimento Reprovado';
    worksheet.mergeCells('E2:F2');
    worksheet.getCell('E2').value = '☐ Produção';

    ['A2', 'C2', 'E2'].forEach(cellRef => {
        const cell = worksheet.getCell(cellRef);
        cell.font = { size: 11 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    }

    // Row 3: Date and Responsible
    currentRow = 3;
    const row3 = worksheet.getRow(currentRow);
    row3.height = 25;

    worksheet.mergeCells('A3:C3');
    worksheet.getCell('A3').value = 'Data: ____/____/____';
    worksheet.mergeCells('D3:F3');
    worksheet.getCell('D3').value = 'Visto Responsável:';

    ['A3', 'D3'].forEach(cellRef => {
        const cell = worksheet.getCell(cellRef);
        cell.font = { size: 11 };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
    });

    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    }

    // Row 4: Section title
    currentRow = 4;
    worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'INFORMAÇÕES SOBRE O PRODUTO FABRICADO';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    worksheet.getCell(`A${currentRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(currentRow).height = 25;
    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    }

    // Row 5: Calculated KG/L, Loss, Programmed KG/L
    currentRow = 5;
    worksheet.getRow(currentRow).height = 25;
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const calcDisplay = Number(pesoCalculado);
    worksheet.getCell(`A${currentRow}`).value = `CALCULADA KG/L: ${calcDisplay}`;
    worksheet.getCell(`A${currentRow}`).numFmt = '0.000###';

    worksheet.getCell(`D${currentRow}`).value = `PERDA: ${perdaPercentual}%`;

    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    const progDisplay = Number(pesoProgramado);
    worksheet.getCell(`E${currentRow}`).value = `PROGRAMADA KG/L: ${progDisplay}`;
    worksheet.getCell(`E${currentRow}`).numFmt = '0.000###';

    for (let c = 1; c <= 6; c++) {
        const cell = worksheet.getCell(currentRow, c);
        cell.font = { bold: true, size: 11 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }

    // ========================================================================
    // ROW 6: Product and Lot - REMOVED BOTTOM BORDER FOR A6 AND D6
    // ========================================================================
    currentRow = 6;
    worksheet.getRow(currentRow).height = 25;

    // Merge A6:C6
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const mergedCellA = worksheet.getCell(`A${currentRow}`);
    mergedCellA.value = `PRODUTO: ${item.productCode} - ${item.productName}`;
    mergedCellA.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    mergedCellA.font = { bold: true, size: 11 };
    // Set border for merged range A6:C6 - NO BOTTOM BORDER
    mergedCellA.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
        // bottom intentionally omitted
    };

    // Merge D6:F6
    worksheet.mergeCells(`D${currentRow}:F${currentRow}`);
    const mergedCellD = worksheet.getCell(`D${currentRow}`);
    mergedCellD.value = `LOTE: ${item.lote || '___________'}`;
    mergedCellD.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellD.font = { bold: true, size: 11 };
    // Set border for merged range D6:F6 - NO BOTTOM BORDER
    mergedCellD.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
        // bottom intentionally omitted
    };

    // ========================================================================
    // ROW 7: Tank, Temp Ambiente, and OP - REMOVED BOTTOM BORDER FOR A7 AND D7, AND RIGHT BORDER FOR D7
    // ========================================================================
    currentRow = 7;
    worksheet.getRow(currentRow).height = 25;

    // Merge A7:C7
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const mergedCellA7 = worksheet.getCell(`A${currentRow}`);
    mergedCellA7.value = 'TANQUE: ';
    mergedCellA7.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellA7.font = { size: 11 };
    // Set border for merged range A7:C7 - NO BOTTOM BORDER
    mergedCellA7.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
        // bottom intentionally omitted
    };

    // D7 - NOT MERGED, just a single cell
    const cellD7 = worksheet.getCell(`D${currentRow}`);
    cellD7.value = 'TEMPERATURA AMBIENTE: ';
    cellD7.alignment = { horizontal: 'left', vertical: 'middle' };
    cellD7.font = { size: 11 };
    // NO BOTTOM BORDER, NO RIGHT BORDER for D7
    cellD7.border = {
        top: { style: 'thin' },
        left: { style: 'medium' }
        // bottom and right intentionally omitted
    };

    // Merge E7:F7
    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    const mergedCellE7 = worksheet.getCell(`E${currentRow}`);
    mergedCellE7.value = `OP: ${order.opCode || '___________'}`;
    mergedCellE7.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellE7.font = { size: 11 };
    // Set border for merged range E7:F7 - NO LEFT BORDER (to connect with D7)
    mergedCellE7.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'medium' }
        // left intentionally omitted
    };

    // ========================================================================
    // ROW 8: Manufacturing Date and Expiry - REMOVED BOTTOM BORDER FOR A8 AND D8
    // ========================================================================
    currentRow = 8;
    worksheet.getRow(currentRow).height = 25;

    // Merge A8:C8
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const mergedCellA8 = worksheet.getCell(`A${currentRow}`);
    mergedCellA8.value = 'DATA DE FABRICAÇÃO: ____/____/____';
    mergedCellA8.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellA8.font = { size: 11 };
    // Set border for merged range A8:C8 - NO BOTTOM BORDER
    mergedCellA8.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
        // bottom intentionally omitted
    };

    // Merge D8:F8
    worksheet.mergeCells(`D${currentRow}:F${currentRow}`);
    const mergedCellD8 = worksheet.getCell(`D${currentRow}`);
    mergedCellD8.value = 'DATA DE VALIDADE: ____/____/____';
    mergedCellD8.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellD8.font = { size: 11 };
    // Set border for merged range D8:F8 - NO BOTTOM BORDER
    mergedCellD8.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
        // bottom intentionally omitted
    };

    // ========================================================================
    // ROW 9: Manipulacao Schedules - normal borders
    // ========================================================================
    currentRow = 9;
    worksheet.getRow(currentRow).height = 25;
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'HORÁRIO ÍNICIO MANIPULAÇÃO:  ____:____:____';

    worksheet.mergeCells(`D${currentRow}:F${currentRow}`);
    worksheet.getCell(`D${currentRow}`).value = 'HORÁRIO FIM MANIPULAÇÃO:  ____:____:____';

    // Set borders for row 9 - now we have a bottom border that separates from the header
    worksheet.getCell(`A${currentRow}`).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    worksheet.getCell(`D${currentRow}`).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };

    // Row 10: Table headers
    currentRow = 10;
    const headerRow = worksheet.getRow(currentRow);
    headerRow.height = 30;
    const headers = ['%', 'Código', 'Lote/Fornecedor', 'INCI Matéria Prima / Nome Comercial', 'UN', 'Quant.'];
    headers.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });
    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    }

    currentRow = 11;
    const itemsStartRow = currentRow;

    // Material rows by phase
    faseKeys.forEach(faseKey => {
        const faseMateriais = fases[faseKey];

        if (faseKeys.length > 0) {
            worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = `FASE ${faseKey}`;
            worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
            worksheet.getCell(`A${currentRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ff5480ba' } };
            worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
            for (let c = 1; c <= 6; c++) {
                worksheet.getCell(currentRow, c).border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
            }
            currentRow++;
        }

        faseMateriais.forEach(mp => {
            const pCell = worksheet.getCell(`A${currentRow}`);
            pCell.value = mp.quantity ? Number(parseFloat(mp.quantity)) : '';
            pCell.numFmt = '#,##0.000###';

            const codigoCell = worksheet.getCell(`B${currentRow}`);
            codigoCell.value = mp.code || '';
            codigoCell.font = { bold: true, size: 12 };
            codigoCell.alignment = { horizontal: 'center', vertical: 'middle' };

            worksheet.getCell(`C${currentRow}`).value = '';

            const nameCell = worksheet.getCell(`D${currentRow}`);
            let displayName = mp.commercialName || mp.inciName || '';
            if (mp.isSemiFinished) {
                displayName = `[SA] ${displayName}`;
            }
            nameCell.value = displayName;

            const unCell = worksheet.getCell(`E${currentRow}`);
            unCell.value = 'KG';
            unCell.alignment = { horizontal: 'center', vertical: 'middle' };

            const qCell = worksheet.getCell(`F${currentRow}`);
            qCell.value = mp.needed ? Number(mp.needed) : '';
            qCell.numFmt = '#,##0.0000######';

            for (let c = 1; c <= 6; c++) {
                const cell = worksheet.getCell(currentRow, c);
                cell.font = { size: 12 };
                if (c !== 2 && c !== 5) {
                    cell.alignment = { horizontal: c === 6 ? 'right' : 'left', vertical: 'middle', wrapText: true };
                }
                cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
            }
            currentRow++;
        });
    });

    // TOTAL ROW: Only merge B, C, and D
    const percentTotalCell = worksheet.getCell(`A${currentRow}`);
    percentTotalCell.value = { formula: `SUM(A${itemsStartRow}:A${currentRow - 1})` };
    percentTotalCell.font = { bold: true, size: 12 };
    percentTotalCell.numFmt = '#,##0.000###';
    percentTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells(`B${currentRow}:D${currentRow}`);
    worksheet.getCell(`B${currentRow}`).value = 'TOTAL';
    worksheet.getCell(`B${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`B${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.getCell(`E${currentRow}`).value = 'KG';
    worksheet.getCell(`E${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };

    const totalCell = worksheet.getCell(`F${currentRow}`);
    totalCell.value = { formula: `SUM(F${itemsStartRow}:F${currentRow - 1})` };
    totalCell.font = { bold: true, size: 12 };
    totalCell.numFmt = '#,##0.0000######';
    totalCell.alignment = { horizontal: 'right', vertical: 'middle' };

    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Responsible for Weighing
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Responsável pela Pesagem:';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    worksheet.getCell(`E${currentRow}`).value = 'Data: ';
    worksheet.getCell(`E${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Responsible for Manufacturing
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Responsável pela Fabricação: ';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    worksheet.getCell(`E${currentRow}`).value = 'Data: ';
    worksheet.getCell(`E${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Tolerance section
    worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'TOLERÂNCIA PARA PESAGEM';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    worksheet.getCell(`A${currentRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(currentRow).height = 25;
    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Tolerância máxima permitida de pesagem: ±1%';
    worksheet.getCell(`A${currentRow}`).font = { size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Preparation method section
    worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'MODO DE PREPARO';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    worksheet.getCell(`A${currentRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(currentRow).height = 25;
    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Preparation steps
    if (preparationSteps && preparationSteps.length > 0) {
        const sortedSteps = [...preparationSteps].sort((a, b) => (a.order || 0) - (b.order || 0));
        sortedSteps.forEach((step, index) => {
            worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = `${index + 1}. ${step.description || ''}`;
            worksheet.getCell(`A${currentRow}`).font = { size: 10 };
            worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

            for (let c = 1; c <= 6; c++) {
                worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
            }

            const stepRow = worksheet.getRow(currentRow);
            stepRow.height = (step.description && step.description.length > 80) ? 35 : 20;
            currentRow++;
        });
    } else {
        for (let i = 1; i <= 5; i++) {
            worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = `${i}.`;
            worksheet.getCell(`A${currentRow}`).font = { size: 10 };
            worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
            worksheet.getRow(currentRow).height = 20;
            for (let c = 1; c <= 6; c++) {
                worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
            }
            currentRow++;
        }
    }

    // Observations (4 lines)
    for (let i = 1; i <= 4; i++) {
        worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = i === 1 ? 'Observações: ' : '';
        worksheet.getCell(`A${currentRow}`).font = { size: 10 };
        worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
        worksheet.getRow(currentRow).height = 25;
        for (let c = 1; c <= 6; c++) {
            worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
        }
        currentRow++;
    }

    currentRow++;

    // Approval line
    const aprovRow = worksheet.getRow(currentRow);
    aprovRow.height = 45;

    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Elaborado por: Kátia Fernandes\nData: 17/06/2026';
    worksheet.getCell(`A${currentRow}`).font = { size: 9 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = 'Revisado por: Magali Murro\nData: 17/06/2026';
    worksheet.getCell(`C${currentRow}`).font = { size: 9 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    worksheet.getCell(`E${currentRow}`).value = 'Aprovado por: Nisrine El Hazzat\nData: 17/06/2026';
    worksheet.getCell(`E${currentRow}`).font = { size: 9 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    for (let c = 1; c <= 6; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }

    // Print setup
    worksheet.pageSetup.orientation = 'portrait';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 1;
    worksheet.pageSetup.paperSize = 9;
};

/**
 * Exports the manufacturing process document for a production order
 */
export const exportProcessoManipulacao = async ({
    order,
    allRawMaterials = [],
    allCategories = [],
    allItems = [],
    allProducts = [],
    allProductTypes = [],
    showNotification
}) => {
    try {
        if (!order || !order.items || order.items.length === 0) {
            if (showNotification) showNotification("Ordem sem produtos para gerar o processo de manipulação.", 'warning');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const logoImageId = await loadLogo(workbook);

        const hasDirectProductData = order.productData && Object.keys(order.productData).length > 0;

        for (let itemIndex = 0; itemIndex < order.items.length; itemIndex++) {
            const item = order.items[itemIndex];
            let productData;

            if (hasDirectProductData) {
                productData = order.productData;
            } else {
                try {
                    const productSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, item.productId));
                    if (!productSnap.exists()) {
                        console.warn(`Product ${item.productCode} not found in Firestore, skipping...`);
                        continue;
                    }
                    productData = productSnap.data();
                } catch (fetchError) {
                    console.error('Error fetching product from Firestore:', fetchError);
                    if (showNotification) {
                        showNotification(`Erro ao buscar dados do produto ${item.productCode}: ${fetchError.message}`, 'error');
                    }
                    continue;
                }
            }

            const manipulationData = order.manipulationData || null;

            createProductSheet(workbook, logoImageId, {
                item,
                productData,
                allRawMaterials: allRawMaterials || [],
                allCategories: allCategories || [],
                allItems: allItems || [],
                allProducts: allProducts || [],
                allProductTypes: allProductTypes || [],
                order,
                sheetIndex: itemIndex,
                manipulationData
            });
        }

        if (workbook.worksheets.length === 0) {
            if (showNotification) showNotification("Não foi possível gerar o processo de manipulação.", 'error');
            return;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fileName = `Processo_Manipulacao_${order.opCode || 'OP'}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);

        if (showNotification) {
            showNotification(`Processo de manipulação gerado com sucesso para ${workbook.worksheets.length} produto(s)!`, 'success');
        }

    } catch (error) {
        console.error("Erro ao gerar processo de manipulação:", error);
        if (showNotification) {
            showNotification(`Erro ao gerar processo: ${error.message}`, 'error');
        }
    }
};